/* =========================================================
   Pantalla de acceso, con todos los flujos que se decidieron:
   credenciales, 2FA, SSO de Microsoft y recuperación de contraseña.

   Sirve a los dos modos y la diferencia importa:

     · en demostración TODO se simula, incluido el enlace del correo, que
       se «pulsa» con un botón. Es lo que permite enseñar el flujo entero
       sin servidor.
     · contra la API el enlace llega de verdad al correo, y esta pantalla
       lo recibe: cuando la dirección trae un `?token=`, se comprueba
       contra el servidor y se pide la contraseña nueva.

   El mismo formulario sirve para restablecer y para una invitación. Lo
   que cambia es el texto, porque a quien acaba de recibir su cuenta
   decirle «restablece tu contraseña» le da a entender que ya tenía una.
   ========================================================= */
import React from 'react';
import type { DemoUser } from '@/types';
import { ic, sized } from '@/components/Icons';
import { Logo, PasswordInput } from '@/components/ui';
import { DEMO_USERS, IS_API } from '@/data';
import {
  capacidadesAcceso, establecerPassword, pedirEnlace, revisarEnlace,
  type EnlaceRevisado,
} from '@/lib/api';
import { attemptSignIn } from '@/lib/session';
import { useI18n } from '@/i18n';
import { BackLink, MsLogo, PaneHeader, PasswordStrength, maskEmail, passwordScore } from './parts';

type Mode =
  | 'login' | 'forgot' | 'sent' | 'reset' | 'twofa' | 'microsoft'
  /** Llegó con un enlace en la dirección y se está comprobando. */
  | 'enlace'
  /** El enlace no sirve, y se dice por qué. */
  | 'enlace-malo';

/** El token del enlace del correo, si la dirección trae uno.
 *
 *  Se lee de la dirección y no de una ruta del router porque la pantalla
 *  de acceso se pinta antes de que haya sesión, así que aquí no hay
 *  router que consultar todavía. Se mira también el hash porque la
 *  versión de un solo archivo usa HashRouter. */
function tokenDeLaUrl(): string {
  if (typeof window === 'undefined') return '';
  const directo = new URLSearchParams(window.location.search).get('token');
  if (directo) return directo;
  const hash = window.location.hash.replace(/^#/, '');
  const q = hash.indexOf('?');
  return q >= 0 ? (new URLSearchParams(hash.slice(q)).get('token') ?? '') : '';
}

/** Quita el token de la barra de direcciones.
 *
 *  Se llama en cuanto se usa: un enlace que abre una cuenta no debe
 *  quedarse en el historial del navegador ni en la dirección que alguien
 *  copia para enseñar la pantalla. */
function limpiarUrl(): void {
  if (typeof window === 'undefined') return;
  window.history.replaceState({}, '', window.location.pathname);
}

/* ---------- LoginView ---------- */

interface LoginViewProps {
  onLogin: (u: DemoUser, mustChangePassword?: boolean) => void;
  onForgot: () => void;
  onTwoFA: (u: DemoUser) => void;
  onMicrosoft: (u: DemoUser) => void;
  shake?: boolean;
}

function LoginView({ onLogin, onForgot, onTwoFA, onMicrosoft, shake }: LoginViewProps) {
  const { lang, t } = useI18n();
  const users = DEMO_USERS;
  // Los campos empiezan VACÍOS, también en la demostración. Venían rellenos
  // con la cuenta de ejemplo y la contraseña «demo»: en el portal real eso es
  // el correo de otra persona escrito en el campo de quien va a entrar, y en
  // la demostración enseña a la gente a pulsar «Iniciar sesión» sin leer.
  // Para no dejar la demostración sin forma de entrar, las credenciales de
  // ejemplo se enseñan como texto debajo del formulario, no dentro de él.
  const [u, setU] = React.useState('');
  const [p, setP] = React.useState('');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // el servidor puede pedir el segundo factor después de validar la contraseña
  const [totp, setTotp] = React.useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await attemptSignIn(u, p, totp ?? undefined);
      if (res.kind === 'totp') {
        setTotp('');
        setErr(lang === 'es' ? 'Introduce el código de verificación.' : 'Enter your verification code.');
        return;
      }
      if (res.kind === 'error') {
        // en modo demo el mensaje es genérico; con backend, el del servidor
        setErr(
          res.message === 'credentials'
            ? (lang === 'es' ? 'Credenciales incorrectas.' : 'Wrong credentials.')
            : res.message,
        );
        return;
      }
      setErr('');
      onLogin(res.user, res.mustChangePassword);
    } finally {
      setBusy(false);
    }
  }

  const fieldStyle = (filled: string) => ({
    padding: '11px 12px',
    borderRadius: 8,
    fontSize: 14,
    border: '1px solid ' + (filled ? '#7FB8E8' : 'var(--line-2)'),
    background: filled ? '#EAF4FD' : 'var(--surface)',
    transition: 'background .18s ease, border-color .18s ease',
  });

  return (
    <form
      onSubmit={submit}
      style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18, animation: shake ? 'shake .35s' : 'none' }}
    >
      <PaneHeader title={t('common.signin')} />

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{t('common.username')}</span>
        <input value={u} onChange={(e) => setU(e.target.value)} autoComplete="username" style={fieldStyle(u)} />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{t('common.password')}</span>
        {/* El ojo evita el bucle de «me la escribieron mal / la tecleé mal»
            con las contraseñas largas que entrega ETC a mano. Nace apagado:
            revelar es una decisión de quien mira, no el estado por defecto. */}
        <PasswordInput
          value={p}
          onChange={(e) => setP(e.target.value)}
          autoComplete="current-password"
          style={fieldStyle(p)}
        />
        <button
          type="button"
          onClick={onForgot}
          style={{ fontSize: 12, alignSelf: 'flex-end', marginTop: 2, background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', padding: 0, fontWeight: 600 }}
        >
          {t('login.forgot')}
        </button>
      </label>

      {totp !== null ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
            {lang === 'es' ? 'Código de verificación' : 'Verification code'}
          </span>
          <input
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            style={{ ...fieldStyle(totp), letterSpacing: '.4em', fontFamily: 'var(--mono)' }}
          />
        </label>
      ) : null}

      {err ? <div style={{ color: 'var(--err)', fontSize: 12, fontWeight: 500 }}>{err}</div> : null}

      {/* Solo en la demostración, y como texto: quien quiera probarla tiene
          las credenciales a mano, pero el campo sigue vacío hasta que alguien
          decida escribir en él. */}
      {!IS_API ? (
        <div
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', padding: '9px 11px',
            background: 'var(--surface-2)', border: '1px dashed var(--line-2)', borderRadius: 8,
            fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5,
          }}
        >
          <span style={{ color: 'var(--info)', flexShrink: 0, display: 'inline-flex' }}>{sized(ic.info, 13)}</span>
          <span>
            {lang === 'es' ? 'Versión de demostración. Puedes entrar con ' : 'Demonstration version. Sign in with '}
            <b className="mono">{users[0].username}</b>
            {lang === 'es' ? ' y la contraseña ' : ' and the password '}
            <b className="mono">demo</b>.
          </span>
        </div>
      ) : null}

      <button className="btn primary" type="submit" disabled={busy} style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}>
        {t('common.signin')} {ic.arrowR}
      </button>

      {/* Estos dos botones son de la maqueta y SOLO existen en la
          demostración.

          Ninguno habla con nadie: el de 2FA salta directo a la pantalla del
          código, y el de Microsoft dibuja una pantalla de consentimiento
          nuestra —logo incluido— y al aceptar entra como el primer usuario
          de ejemplo. Están para enseñar cómo se verá el flujo, no para
          autenticar.

          En el portal real el de 2FA ya estaba apagado, pero el de Microsoft
          no: quedaba visible y dejaba a cualquiera dentro de un cascarón que
          decía «Admin ETC». Sin datos —el servidor rechaza cada petición sin
          token— pero indistinguible de una sesión a ojos de quien lo pulsa.

          Se ocultan enteros, con su separador, en vez de dejarlos en gris:
          un botón apagado y sin explicación invita a preguntar qué se ha
          roto. Cuando el inicio de sesión con Microsoft sea de verdad,
          volverá aquí conectado al tenant de ETC y visible solo si el
          servidor confirma que está configurado. */}
      {!IS_API ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700 }}>{t('common.or')}</span>
            <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              className="btn"
              onClick={() => {
                const user = users.find((x) => x.username === u && x.password === p);
                if (!user) {
                  setErr(lang === 'es' ? 'Ingresa credenciales válidas para usar 2FA.' : 'Enter valid credentials to use 2FA.');
                  return;
                }
                setErr('');
                onTwoFA(user);
              }}
              style={{ justifyContent: 'space-between', padding: '10px 14px' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {ic.lock} {t('login.signin_2fa')}
              </span>
              {ic.arrowR}
            </button>

            <button
              type="button"
              className="btn"
              onClick={() => onMicrosoft(users.find((x) => x.username === u) || users[0])}
              style={{ justifyContent: 'space-between', padding: '10px 14px' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MsLogo s={14} />
                {t('login.signin_ms')}
              </span>
              {ic.arrowR}
            </button>
          </div>
        </>
      ) : null}
    </form>
  );
}

/* ---------- ForgotView ---------- */

/** Pantalla honesta para cuando NO hay envío de correo.
 *
 *  El flujo de recuperación estaba entero —pedir el correo, «te mandamos
 *  un enlace», restablecer— y no mandaba nada: la vista solo cambiaba de
 *  paso. Con datos de ejemplo eso es una maqueta; con socios de verdad es
 *  dejar tirado a quien no puede entrar, diciéndole que mire su bandeja.
 *
 *  Mientras el correo no esté conectado, esto dice la verdad y da la
 *  salida que existe: llamar a ETC. */
function SinCorreoView({ onBack }: { onBack: () => void }) {
  const { lang, t } = useI18n();
  return (
    <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PaneHeader title={t('login.forgot_title')} />
      <BackLink onClick={onBack} label={t('login.back_to_login')} />
      <div
        style={{
          display: 'flex', gap: 10, alignItems: 'flex-start', padding: '14px 16px',
          border: '1px solid var(--warn)', borderRadius: 10, fontSize: 13.5, lineHeight: 1.55,
        }}
      >
        <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--warn)' }}>{ic.warn}</span>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {lang === 'es'
              ? 'El restablecimiento por correo todavía no está disponible'
              : 'Password reset by email is not available yet'}
          </div>
          {lang === 'es'
            ? 'El portal todavía no manda correos, así que no podemos enviarte un enlace. Escribe al equipo de ETC Iberoamérica o llámales: te darán una contraseña nueva y el portal te pedirá cambiarla en cuanto entres.'
            : 'The portal does not send email yet, so we cannot send you a link. Contact the ETC Iberoamérica team: they will give you a new password, and the portal will ask you to change it as soon as you sign in.'}
        </div>
      </div>
      <button className="btn" onClick={onBack} style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}>
        {t('login.back_to_login')}
      </button>
    </div>
  );
}

function ForgotView({ onBack, onSent, prefill }: { onBack: () => void; onSent: (email: string) => void; prefill: string }) {
  const { lang, t } = useI18n();
  const [email, setEmail] = React.useState(prefill || '');
  const [err, setErr] = React.useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErr(lang === 'es' ? 'Email no válido.' : 'Invalid email.');
      return;
    }
    onSent(email.trim());
  }

  return (
    <form onSubmit={submit} style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PaneHeader title={t('login.forgot_title')} />
      <BackLink onClick={onBack} label={t('login.back_to_login')} />

      <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>{t('login.forgot_help')}</p>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{lang === 'es' ? 'Correo electrónico' : 'Email'}</span>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErr('');
          }}
          placeholder="tu@empresa.com"
          autoComplete="email"
          autoFocus
          style={{ padding: '11px 12px', borderRadius: 8, border: '1px solid ' + (err ? 'var(--err)' : 'var(--line-2)'), fontSize: 14 }}
        />
        {err ? <span style={{ fontSize: 12, color: 'var(--err)' }}>{err}</span> : null}
      </label>

      <button className="btn primary" type="submit" style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}>
        {ic.mail} {t('login.send_link')}
      </button>

      <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.55, padding: '10px 12px', borderRadius: 8, background: 'var(--surface-2)', border: '1px dashed var(--line-2)' }}>
        {lang === 'es'
          ? 'Por seguridad, te confirmaremos el envío incluso si el correo no está registrado. Esto evita revelar cuentas existentes.'
          : 'For security we will confirm the send even if the email is not registered. This prevents disclosing existing accounts.'}
      </div>
    </form>
  );
}

/* ---------- SentView ---------- */

function SentView({
  onBack, onSimulate, email, demo = true, onReenviar,
}: {
  onBack: () => void;
  onSimulate: () => void;
  email: string;
  /** El atajo que simula pulsar el enlace. Solo en demostración: contra la
   *  API el enlace llega de verdad, y un botón que se lo salta sería una
   *  puerta abierta en la pantalla de acceso. */
  demo?: boolean;
  /** Reenvía el enlace. Sin esto el botón solo cambia el texto, que es lo
   *  que hacía en demostración. */
  onReenviar?: () => void;
}) {
  const { lang, t } = useI18n();
  const [resent, setResent] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const i = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(i);
  }, [cooldown]);

  return (
    <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PaneHeader title={t('login.sent_title')} />
      <BackLink onClick={onBack} label={t('login.back_to_login')} />

      <div
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14,
          padding: '24px 18px', background: 'var(--surface-2)', borderRadius: 12, border: '1px solid var(--line)',
        }}
      >
        <div style={{ width: 56, height: 56, borderRadius: 99, background: 'var(--brand-primary-l)', color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {sized(ic.mail, 26)}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{lang === 'es' ? 'Enlace enviado' : 'Link sent'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5, maxWidth: 320 }}>
            {t('login.sent_help', { email })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setResent(true);
            setCooldown(30);
            onReenviar?.();
          }}
          disabled={cooldown > 0}
          style={{ justifyContent: 'center', padding: '10px 14px' }}
        >
          {ic.refresh} {cooldown > 0 ? `${t('login.sent_resend')} · ${cooldown}s` : t('login.sent_resend')}
        </button>
        {resent ? (
          <span style={{ fontSize: 11, color: 'var(--ok)', textAlign: 'center' }}>
            {ic.check} {lang === 'es' ? 'Enlace reenviado' : 'Link resent'}
          </span>
        ) : null}
        <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center', marginTop: 4 }}>{t('login.sent_no_email')}</div>
      </div>

      {/* demo affordance: simulate clicking the email link */}
      <div hidden={!demo} style={{ marginTop: 6, padding: 12, background: 'var(--surface-2)', borderRadius: 10, border: '1px dashed var(--line-2)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
          {lang === 'es' ? 'Modo demo' : 'Demo mode'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
          {lang === 'es'
            ? 'En producción el usuario haría clic en el enlace del correo. Aquí lo simulamos:'
            : 'In production the user would click the link in the email. We simulate it here:'}
        </div>
        <button type="button" className="btn primary sm" onClick={onSimulate} style={{ width: '100%', justifyContent: 'center' }}>
          {ic.arrowR} {t('login.simulate_link')}
        </button>
      </div>
    </div>
  );
}

/* ---------- enlace que no sirve ---------- */

/** Un enlace caducado, ya usado o que no existe.
 *
 *  Los tres se explican distinto a propósito: «caducó» y «ya se usó»
 *  tienen remedio —pedir otro— y decirlo evita que quien lo recibió crea
 *  que el portal está roto. «No es válido» se queda vago porque puede ser
 *  un enlace inventado, y ahí no hay nada que aclarar. */
function EnlaceMaloView({
  motivo, onPedirOtro, onBack,
}: {
  motivo: 'desconocido' | 'usado' | 'caducado' | 'red';
  onPedirOtro: () => void;
  onBack: () => void;
}) {
  const { lang, t } = useI18n();
  const es = lang === 'es';

  const texto: Record<typeof motivo, { titulo: string; cuerpo: string }> = {
    caducado: {
      titulo: es ? 'El enlace caducó' : 'The link expired',
      cuerpo: es
        ? 'Los enlaces duran poco a propósito, porque abren una cuenta. Pide otro y te llega uno nuevo.'
        : 'Links are short-lived on purpose, because they open an account. Request another and a new one will arrive.',
    },
    usado: {
      titulo: es ? 'Ese enlace ya se usó' : 'That link was already used',
      cuerpo: es
        ? 'Cada enlace sirve una sola vez. Si ya pusiste tu contraseña, entra con ella. Si no fuiste tú, pide otro y avisa a ETC Iberoamérica.'
        : 'Each link works once. If you already set your password, sign in with it. If it was not you, request another and tell ETC Iberoamérica.',
    },
    desconocido: {
      titulo: es ? 'El enlace no es válido' : 'The link is not valid',
      cuerpo: es
        ? 'Puede que se haya cortado al copiarlo del correo. Ábrelo pulsando el botón del mensaje, o pide uno nuevo.'
        : 'It may have been cut when copied from the email. Open it by pressing the button in the message, or request a new one.',
    },
    red: {
      titulo: es ? 'No se pudo comprobar el enlace' : 'The link could not be checked',
      cuerpo: es
        ? 'No hubo respuesta del servidor. Vuelve a intentarlo en un momento.'
        : 'The server did not respond. Try again in a moment.',
    },
  };
  const { titulo, cuerpo } = texto[motivo];

  return (
    <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PaneHeader title={es ? 'Enlace de acceso' : 'Access link'} />
      <BackLink onClick={onBack} label={t('login.back_to_login')} />
      <div
        style={{
          display: 'flex', gap: 10, alignItems: 'flex-start', padding: '14px 16px',
          border: '1px solid var(--warn)', borderRadius: 10, fontSize: 13.5, lineHeight: 1.55,
        }}
      >
        <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--warn)' }}>{ic.warn}</span>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{titulo}</div>
          {cuerpo}
        </div>
      </div>
      {motivo === 'red' ? null : (
        <button
          className="btn primary"
          onClick={onPedirOtro}
          style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}
        >
          {ic.mail} {es ? 'Pedir un enlace nuevo' : 'Request a new link'}
        </button>
      )}
      <button className="btn" onClick={onBack} style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}>
        {t('login.back_to_login')}
      </button>
    </div>
  );
}

/* ---------- ResetView ---------- */

function ResetView({
  onDone, onBack, email, modo = 'reset', busy = false, errorServidor = '',
}: {
  /** Recibe la contraseña. En demostración se ignora; contra la API es lo
   *  que se manda. */
  onDone: (password: string) => void;
  onBack: () => void;
  email: string;
  /** Una invitación no «restablece» nada: es la primera contraseña de esa
   *  cuenta, y decirle «restablecer» a quien acaba de recibirla confunde. */
  modo?: 'reset' | 'invitacion';
  busy?: boolean;
  /** Lo que dijo el servidor. Va aparte de la validación local porque son
   *  cosas distintas: «esa contraseña es débil» lo decide el navegador,
   *  «ese enlace ya se usó» solo lo sabe el servidor. */
  errorServidor?: string;
}) {
  const { lang, t } = useI18n();
  const es = lang === 'es';
  const invita = modo === 'invitacion';
  const [p1, setP1] = React.useState('');
  const [p2, setP2] = React.useState('');
  const [show, setShow] = React.useState(false);
  const [err, setErr] = React.useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (passwordScore(p1) < 3) {
      setErr(t('login.password_weak'));
      return;
    }
    if (p1 !== p2) {
      setErr(t('login.password_mismatch'));
      return;
    }
    setErr('');
    onDone(p1);
  }

  return (
    <form onSubmit={submit} style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PaneHeader
        title={invita
          ? (es ? 'Elige tu contraseña' : 'Choose your password')
          : t('login.reset_title')}
      />
      <BackLink onClick={onBack} label={t('login.back_to_login')} />

      <div style={{ padding: '10px 12px', background: 'var(--brand-primary-l)', borderRadius: 8, fontSize: 12, color: 'var(--brand-primary-d)', display: 'flex', alignItems: 'center', gap: 8 }}>
        {sized(ic.user, 14)}
        <span>
          {invita
            ? (es ? 'Cuenta' : 'Account')
            : (es ? 'Restableciendo contraseña para' : 'Resetting password for')}{' '}
          <strong>{email || 'tu@empresa.com'}</strong>
        </span>
      </div>

      <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 13 }}>
        {invita
          ? (es
              ? 'ETC Iberoamérica te dio de alta en el portal. Elige una contraseña y entra: es la única que tendrás que recordar.'
              : 'ETC Iberoamérica registered you in the portal. Choose a password and sign in: it is the only one you will need to remember.')
          : t('login.reset_help')}
      </p>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{t('login.new_password')}</span>
        <div style={{ position: 'relative' }}>
          <input
            type={show ? 'text' : 'password'}
            value={p1}
            onChange={(e) => {
              setP1(e.target.value);
              setErr('');
            }}
            autoComplete="new-password"
            autoFocus
            style={{ width: '100%', padding: '11px 76px 11px 12px', borderRadius: 8, border: '1px solid var(--line-2)', fontSize: 14 }}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            style={{
              position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', color: 'var(--text-2)', fontSize: 11, fontWeight: 600,
              cursor: 'pointer', padding: '4px 8px',
            }}
          >
            {show ? t('login.pw_hide') : t('login.pw_show')}
          </button>
        </div>
        <PasswordStrength p={p1} />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{t('login.confirm_password')}</span>
        <input
          type={show ? 'text' : 'password'}
          value={p2}
          onChange={(e) => {
            setP2(e.target.value);
            setErr('');
          }}
          autoComplete="new-password"
          style={{ padding: '11px 12px', borderRadius: 8, border: '1px solid ' + (err && p1 !== p2 ? 'var(--err)' : 'var(--line-2)'), fontSize: 14 }}
        />
        {p2 && p1 !== p2 ? <span style={{ fontSize: 12, color: 'var(--err)' }}>{t('login.password_mismatch')}</span> : null}
      </label>

      {err || errorServidor ? (
        <div style={{ color: 'var(--err)', fontSize: 12, fontWeight: 500 }}>{err || errorServidor}</div>
      ) : null}

      <button
        className="btn primary"
        type="submit"
        disabled={busy || !p1 || !p2 || passwordScore(p1) < 3 || p1 !== p2}
        style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}
      >
        {ic.check}{' '}
        {busy
          ? (es ? 'Guardando…' : 'Saving…')
          : invita
            ? (es ? 'Guardar y entrar' : 'Save and sign in')
            : (es ? 'Actualizar contraseña' : 'Update password')}
      </button>
    </form>
  );
}

/* ---------- TwoFAView (functional; demo code = 123456) ---------- */

function TwoFAView({ user, onBack, onVerified }: { user: DemoUser | null; onBack: () => void; onVerified: () => void }) {
  const { lang, t } = useI18n();
  const LEN = 6;
  const DEMO = '123456';
  const [digits, setDigits] = React.useState<string[]>(Array(LEN).fill(''));
  const [err, setErr] = React.useState('');
  const [cooldown, setCooldown] = React.useState(30);
  const [resent, setResent] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    refs.current[0]?.focus();
  }, []);
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const i = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(i);
  }, [cooldown]);

  const code = digits.join('');

  function setAt(i: number, val: string) {
    setErr('');
    setDigits((d) => {
      const n = d.slice();
      n[i] = val;
      return n;
    });
  }

  function handlePaste(str: string, start = 0) {
    const clean = str.replace(/\D/g, '').slice(0, LEN - start);
    if (!clean) return;
    setDigits((d) => {
      const n = d.slice();
      for (let k = 0; k < clean.length; k++) n[start + k] = clean[k];
      return n;
    });
    const next = Math.min(start + clean.length, LEN - 1);
    window.setTimeout(() => refs.current[next]?.focus(), 0);
  }

  function onChange(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.replace(/\D/g, '');
    if (!v) {
      setAt(i, '');
      return;
    }
    if (v.length > 1) {
      handlePaste(v, i);
      return;
    }
    setAt(i, v);
    if (i < LEN - 1) refs.current[i + 1]?.focus();
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < LEN - 1) refs.current[i + 1]?.focus();
  }

  function verify(e?: React.FormEvent) {
    e?.preventDefault();
    if (code.length < LEN) {
      setErr(lang === 'es' ? 'Ingresa los 6 dígitos.' : 'Enter all 6 digits.');
      return;
    }
    setVerifying(true);
    window.setTimeout(() => {
      if (code === DEMO) {
        onVerified();
      } else {
        setVerifying(false);
        setErr(lang === 'es' ? 'Código incorrecto. Inténtalo de nuevo.' : 'Wrong code. Try again.');
        setDigits(Array(LEN).fill(''));
        refs.current[0]?.focus();
      }
    }, 650);
  }

  return (
    <form onSubmit={verify} style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18, animation: err ? 'shake .35s' : 'none' }}>
      <PaneHeader title={t('login.twofa_title')} />
      <BackLink onClick={onBack} label={t('login.back_to_login')} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', background: 'var(--brand-primary-l)', borderRadius: 10, color: 'var(--brand-primary-d)' }}>
        <span style={{ display: 'inline-flex', marginTop: 1 }}>{sized(ic.lock, 18)}</span>
        <span style={{ fontSize: 13, lineHeight: 1.5 }}>
          {lang === 'es' ? 'Enviamos un código de verificación a ' : 'We sent a verification code to '}
          <strong>{maskEmail(user?.username)}</strong>.
        </span>
      </div>

      {/* code boxes */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={d}
            onChange={(e) => onChange(i, e)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => {
              e.preventDefault();
              handlePaste(e.clipboardData.getData('text'), 0);
            }}
            inputMode="numeric"
            maxLength={1}
            autoComplete="one-time-code"
            aria-label={`${lang === 'es' ? 'dígito' : 'digit'} ${i + 1}`}
            style={{
              width: 52, height: 60, textAlign: 'center', fontSize: 26, fontWeight: 700,
              borderRadius: 10, background: d ? '#EAF4FD' : 'var(--surface)',
              border: '2px solid ' + (err ? 'var(--err)' : d ? '#7FB8E8' : 'var(--line-2)'),
              color: 'var(--text)', transition: 'border-color .15s ease, background .15s ease',
              fontFamily: 'var(--mono)',
            }}
          />
        ))}
      </div>

      {err ? <div style={{ color: 'var(--err)', fontSize: 12, fontWeight: 600 }}>{err}</div> : null}

      <button className="btn primary" type="submit" disabled={verifying || code.length < LEN} style={{ padding: '12px 16px', fontSize: 14, justifyContent: 'center' }}>
        {verifying ? (
          lang === 'es' ? 'Verificando…' : 'Verifying…'
        ) : (
          <>
            {ic.check} {t('login.twofa_verify')}
          </>
        )}
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => {
            setResent(true);
            setCooldown(30);
            setErr('');
          }}
          disabled={cooldown > 0}
          style={{ color: cooldown > 0 ? 'var(--text-3)' : 'var(--brand-primary)', fontWeight: 600 }}
        >
          {cooldown > 0 ? `${t('login.twofa_resend')} · ${cooldown}s` : t('login.twofa_resend')}
        </button>
        {resent && cooldown > 0 ? (
          <span style={{ fontSize: 11, color: 'var(--ok)' }}>
            {sized(ic.check, 11)} {lang === 'es' ? 'Código reenviado' : 'Code resent'}
          </span>
        ) : null}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', cursor: 'pointer' }}>
        <input type="checkbox" style={{ width: 15, height: 15, accentColor: 'var(--brand-primary)' }} />
        {lang === 'es' ? 'Recordar este dispositivo por 30 días' : 'Trust this device for 30 days'}
      </label>

      {/* demo hint */}
      <div style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 10, border: '1px dashed var(--line-2)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
          {lang === 'es' ? 'Modo demo' : 'Demo mode'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {lang === 'es' ? 'Tu código de prueba es' : 'Your test code is'}
          <code style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.18em', background: 'var(--surface)', padding: '3px 10px', borderRadius: 6, border: '1px solid var(--line-2)' }}>
            123456
          </code>
          <button type="button" className="btn sm" onClick={() => handlePaste(DEMO, 0)} style={{ padding: '4px 9px' }}>
            {lang === 'es' ? 'Rellenar' : 'Fill'}
          </button>
        </div>
      </div>
    </form>
  );
}

/* ---------- MicrosoftView (simulated SSO: redirect → consent) ---------- */

function MicrosoftView({ user, onBack, onApprove }: { user: DemoUser | null; onBack: () => void; onApprove: (u: DemoUser | null) => void }) {
  const { lang } = useI18n();
  const [phase, setPhase] = React.useState<'redirect' | 'consent'>('redirect');

  React.useEffect(() => {
    const id = window.setTimeout(() => setPhase('consent'), 1700);
    return () => window.clearTimeout(id);
  }, []);

  const email = user?.username || 'usuario@etc.io';

  if (phase === 'redirect') {
    return (
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22, padding: '40px 18px', textAlign: 'center' }}>
        <MsLogo s={40} />
        <div style={{ width: 38, height: 38, borderRadius: '50%', border: '3px solid var(--line)', borderTopColor: 'var(--brand-primary)', animation: 'etcSpin .9s linear infinite' }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
          {lang === 'es' ? 'Conectando con Microsoft…' : 'Connecting to Microsoft…'}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          {lang === 'es' ? 'Te estamos redirigiendo al portal seguro de tu organización.' : 'Redirecting you to your organization’s secure portal.'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BackLink onClick={onBack} label={lang === 'es' ? 'Cancelar y volver' : 'Cancel and go back'} />
      <div style={{ border: '1px solid var(--line-2)', borderRadius: 12, background: 'var(--surface)', boxShadow: 'var(--shadow-2)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid var(--line)' }}>
          <MsLogo s={22} />
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Microsoft</span>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <span style={{ width: 32, height: 32, borderRadius: 99, background: 'var(--brand-primary-l)', color: 'var(--brand-primary-d)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
              {user?.name ? user.name.split(' ').map((w) => w[0]).slice(0, 2).join('') : 'U'}
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{email}</span>
          </div>

          <h2 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 700, color: 'var(--text)' }}>
            {lang === 'es' ? 'Permisos solicitados' : 'Permissions requested'}
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
            <strong>ETC Connect</strong> {lang === 'es' ? 'desea acceder a tu cuenta institucional:' : 'wants to access your institutional account:'}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {[
              lang === 'es' ? 'Ver tu perfil básico (nombre y foto)' : 'View your basic profile (name and photo)',
              lang === 'es' ? 'Leer tu dirección de correo electrónico' : 'Read your email address',
              lang === 'es' ? 'Mantener el acceso a los datos a los que le diste acceso' : 'Maintain access to data you have given it access to',
            ].map((perm, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: 'var(--text)' }}>
                <span style={{ color: 'var(--ok)', display: 'inline-flex', marginTop: 1 }}>{sized(ic.check, 16)}</span>
                {perm}
              </div>
            ))}
          </div>

          <p style={{ margin: '0 0 18px', fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
            {lang === 'es'
              ? 'Al aceptar, permites que ETC Connect use tu información de acuerdo con sus términos y política de privacidad.'
              : 'By accepting, you allow ETC Connect to use your information per its terms and privacy policy.'}
          </p>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onBack} style={{ padding: '9px 18px' }}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
            <button type="button" className="btn primary" onClick={() => onApprove(user)} style={{ padding: '9px 18px' }}>
              {lang === 'es' ? 'Aceptar' : 'Accept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- LoginScreen wrapper ---------- */

export function LoginScreen({ onLogin, aviso }: {
  onLogin: (u: DemoUser, mustChangePassword?: boolean) => void;
  /** Por qué se volvió aquí, si no fue por decisión propia. */
  aviso?: string;
}) {
  const { lang, t } = useI18n();
  // Con un token en la dirección se arranca directamente comprobándolo: la
  // persona llegó pulsando el botón de un correo, no a iniciar sesión.
  const [tokenEnlace] = React.useState(() => (IS_API ? tokenDeLaUrl() : ''));
  const [mode, setMode] = React.useState<Mode>(tokenEnlace ? 'enlace' : 'login');
  const [forgotEmail, setForgotEmail] = React.useState('');
  const [pendingUser, setPendingUser] = React.useState<DemoUser | null>(null);
  const [successToast, setSuccessToast] = React.useState('');

  /** ¿Puede el portal mandar correo? `null` mientras no se sabe.
   *
   *  Se pregunta una sola vez y solo contra la API: en demostración el
   *  flujo se simula entero y no hay nada que consultar. */
  const [hayCorreo, setHayCorreo] = React.useState<boolean | null>(IS_API ? null : true);
  React.useEffect(() => {
    if (!IS_API) return;
    let vivo = true;
    void capacidadesAcceso().then((c) => { if (vivo) setHayCorreo(c.correo); });
    return () => { vivo = false; };
  }, []);

  /* ---- el enlace del correo ---- */

  const [enlace, setEnlace] = React.useState<EnlaceRevisado | null>(null);
  const [guardando, setGuardando] = React.useState(false);
  const [errorGuardar, setErrorGuardar] = React.useState('');

  React.useEffect(() => {
    if (!tokenEnlace) return;
    let vivo = true;
    void revisarEnlace(tokenEnlace).then((r) => {
      if (!vivo) return;
      setEnlace(r);
      setMode(r.ok ? 'reset' : 'enlace-malo');
      // La dirección se limpia en cuanto se sabe qué había: a partir de
      // aquí el token vive en memoria y no en la barra del navegador.
      limpiarUrl();
    });
    return () => { vivo = false; };
  }, [tokenEnlace]);

  const goLogin = () => {
    setErrorGuardar('');
    setMode('login');
  };

  function handleResetDone() {
    setMode('login');
    setSuccessToast(t('login.reset_success'));
    window.setTimeout(() => setSuccessToast(''), 4000);
  }

  /** Guarda la contraseña nueva usando el token del correo. */
  async function guardarConEnlace(password: string) {
    setGuardando(true);
    setErrorGuardar('');
    const r = await establecerPassword(tokenEnlace, password);
    setGuardando(false);
    if (r.ok) {
      handleResetDone();
      return;
    }
    setErrorGuardar(r.message);
  }

  /** Pide el enlace. El servidor contesta lo mismo exista o no la cuenta,
   *  así que aquí siempre se pasa a «enviado»: distinguir sería revelar
   *  qué correos están registrados. */
  async function pedirEnlaceReal(email: string) {
    setForgotEmail(email);
    setMode('sent');
    const r = await pedirEnlace(email);
    // Solo importa un caso: que el portal no tenga correo. Entonces se
    // retrocede a explicarlo, en vez de dejar a alguien esperando un
    // mensaje que nunca va a llegar.
    if (r.ok && !r.correo) {
      setHayCorreo(false);
      setMode('forgot');
    }
  }

  return (
    <div className="screen-enter auth-grid" style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1.05fr', background: 'var(--bg)' }}>
      {/* left: brand panel */}
      <div
        className="auth-brand"
        style={{
          background: 'linear-gradient(155deg, #1B2330 0%, #2A3441 55%, #353F4F 100%)',
          color: '#fff', padding: '40px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Cada figura se mueve como su geometría deja ver: los anillos
            respiran y el exterior lleva el trazo avanzando —un círculo
            girando no cambia nada—, los cuadros sí giran, el rombo se mece
            y el halo naranja deriva. Se nota al mirar tres segundos y no
            estorba al teclear. Con «reducir movimiento», nada se mueve. */}
        <svg aria-hidden="true" style={{ position: 'absolute', right: -80, bottom: -80, opacity: 0.2 }} width="540" height="540" viewBox="0 0 540 540">
          <g className="auth-fig auth-anillos">
            <circle className="auth-trazo" cx="270" cy="270" r="260" stroke="white" strokeWidth="1.4" fill="none" />
            <circle cx="270" cy="270" r="180" stroke="white" strokeWidth="1" fill="none" />
            <circle cx="270" cy="270" r="100" stroke="white" strokeWidth="1" fill="none" />
          </g>
          <g className="auth-fig auth-cuadros">
            <rect x="120" y="120" width="300" height="300" stroke="white" strokeWidth="1" fill="none" transform="rotate(45 270 270)" />
            <rect x="180" y="180" width="180" height="180" stroke="white" strokeWidth="1" fill="none" />
          </g>
        </svg>
        <div
          aria-hidden="true"
          className="auth-halo"
          style={{
            position: 'absolute', left: -160, top: -120, width: 520, height: 520, borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(242,101,34,0.28), rgba(242,101,34,0) 70%)',
            filter: 'blur(8px)', pointerEvents: 'none',
          }}
        />
        <svg aria-hidden="true" style={{ position: 'absolute', left: -40, top: -40, opacity: 0.17 }} width="300" height="300" viewBox="0 0 100 100">
          <g className="auth-fig auth-rombo">
            <path d="M0 50 L50 0 L100 50 L50 100 Z" stroke="white" strokeWidth="0.8" fill="none" />
            <path d="M10 50 L50 10 L90 50 L50 90 Z" stroke="white" strokeWidth="0.8" fill="none" />
          </g>
        </svg>

        <Logo size={22} light />

        <div style={{ position: 'relative', maxWidth: 520 }}>
          <div className="display" style={{ fontSize: 14, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#F26522', marginBottom: 14 }}>
            ETC Iberoamérica · MCO
          </div>
          <h1 className="display" style={{ fontSize: 46, lineHeight: 1.06, margin: 0, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {t('login.welcome')}
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, opacity: 0.86, marginTop: 14 }}>{t('login.sub')}</p>
          <div
            style={{
              marginTop: 28, display: 'flex', gap: 12, alignItems: 'center',
              padding: '12px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.13)', backdropFilter: 'blur(4px)', maxWidth: 460,
            }}
          >
            <span style={{ color: '#F26522', display: 'inline-flex' }}>{ic.sparkle}</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.92 }}>{t('app.tagline')}</span>
          </div>
        </div>

        <div style={{ position: 'relative', display: 'flex', gap: 18, fontSize: 12, opacity: 0.7, fontWeight: 500 }}>
          <span>
            © {new Date().getFullYear()} ETC Iberoamérica. {lang === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </span>
          <span>·</span>
          <span>{lang === 'es' ? 'Centros Autodesk Learning Partner' : 'Autodesk Learning Partner centers'}</span>
        </div>
      </div>

      {/* right: switching pane */}
      <div className="auth-pane" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, position: 'relative' }}>
        {successToast ? (
          <div
            style={{
              position: 'absolute', top: 20, left: 32, right: 32, display: 'flex', alignItems: 'center', gap: 10,
              background: 'var(--st-read-l)', color: 'var(--st-read)', padding: '10px 14px', borderRadius: 8,
              fontSize: 13, fontWeight: 600, border: '1px solid #BCE7CF',
            }}
          >
            {ic.check} {successToast}
          </div>
        ) : aviso ? (
          // Volver al acceso sin explicación parece un fallo. Con el motivo
          // escrito, la persona sabe que su trabajo no se perdió por un error
          // del portal y que basta con entrar de nuevo.
          <div
            style={{
              position: 'absolute', top: 20, left: 32, right: 32, display: 'flex', alignItems: 'center', gap: 10,
              background: 'var(--st-edit-l)', color: 'var(--st-edit)', padding: '10px 14px', borderRadius: 8,
              fontSize: 13, fontWeight: 600, border: '1px solid var(--st-edit)',
            }}
          >
            {ic.info} {aviso}
          </div>
        ) : null}

        {mode === 'login' ? (
          <LoginView
            onLogin={onLogin}
            onForgot={() => setMode('forgot')}
            onTwoFA={(user) => {
              setPendingUser(user);
              setMode('twofa');
            }}
            onMicrosoft={(user) => {
              setPendingUser(user);
              setMode('microsoft');
            }}
          />
        ) : null}
        {mode === 'twofa' ? <TwoFAView user={pendingUser} onBack={goLogin} onVerified={() => pendingUser && onLogin(pendingUser)} /> : null}
        {mode === 'microsoft' ? (
          <MicrosoftView user={pendingUser} onBack={goLogin} onApprove={(user) => { const u = user || pendingUser; if (u) onLogin(u); }} />
        ) : null}
        {/* Comprobando el enlace del correo. Dura lo que un viaje al
            servidor, pero sin esto la pantalla parpadearía entre el
            formulario y el aviso de enlace inválido. */}
        {mode === 'enlace' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-2)', fontSize: 14 }}>
            {ic.refresh} {lang === 'es' ? 'Comprobando el enlace…' : 'Checking the link…'}
          </div>
        ) : null}

        {mode === 'enlace-malo' && enlace && !enlace.ok ? (
          <EnlaceMaloView
            motivo={enlace.motivo}
            onBack={goLogin}
            onPedirOtro={() => setMode('forgot')}
          />
        ) : null}

        {/* Sin correo configurado no se ofrece el enlace: se explica. Y
            mientras no se sabe (hayCorreo === null) tampoco, porque
            prometer un envío que igual no puede ocurrir es peor que
            esperar medio segundo. */}
        {mode === 'forgot' && IS_API && hayCorreo !== true ? <SinCorreoView onBack={goLogin} /> : null}
        {mode === 'forgot' && (!IS_API || hayCorreo === true) ? (
          <ForgotView
            prefill={forgotEmail}
            onBack={goLogin}
            onSent={(email) => {
              if (IS_API) {
                void pedirEnlaceReal(email);
                return;
              }
              setForgotEmail(email);
              setMode('sent');
            }}
          />
        ) : null}

        {mode === 'sent' ? (
          <SentView
            email={forgotEmail}
            onBack={goLogin}
            demo={!IS_API}
            onReenviar={IS_API ? () => void pedirEnlace(forgotEmail) : undefined}
            onSimulate={() => setMode('reset')}
          />
        ) : null}

        {mode === 'reset' ? (
          <ResetView
            email={enlace?.ok ? enlace.email : forgotEmail}
            modo={enlace?.ok && enlace.kind === 'invitacion' ? 'invitacion' : 'reset'}
            busy={guardando}
            errorServidor={errorGuardar}
            onBack={goLogin}
            onDone={(password) => {
              if (tokenEnlace) {
                void guardarConEnlace(password);
                return;
              }
              handleResetDone();
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
